<!--
  Customizer + preview del informe detallado. Panel de controles (toggles de la plantilla)
  + preview por página. El CONTENIDO de las hojas lo genera el MISMO motor del builder
  (report-page-builder/data/page-builder.render → buildPreview), envuelto en `.rpb` para
  heredar su CSS global. Solo vista (sin botón de config). "Aplicar" aún no persiste aquí.
-->
<template>
  <section>
    <button type="button" class="rt-back" @click="$emit('back')">← {{ $t('back_to_templates') }}</button>
    <div class="rt-split">
      <!-- Panel de controles -->
      <div class="rt-panel" :class="{ locked: template.locked }">
        <div class="rt-phead">
          <div class="rt-pname">{{ $t('template') }}: {{ template.name }}</div>
          <div class="rt-psub">{{ $t('report_templates_customizer_hint') }}</div>
          <div v-if="template.locked" class="rt-locknote">🔒 {{ $t('fixed_template_not_editable') }}</div>
        </div>

        <!-- Lista de páginas (como en el Constructor): solo mostrar/ocultar y reordenar.
             En plantillas fijas (Ejecutivo) los controles quedan bloqueados. -->
        <div class="rt-pbody">
          <ul class="rt-list">
            <li
              v-for="(p, i) in pages"
              :key="p.id"
              class="rt-item"
              :class="{ off: !p.visible, expanded: openConfigId === p.id }">
              <div class="rt-item-row">
                <span class="rt-inum">{{ i + 1 }}</span>
                <span class="rt-iname">{{ displayName(p) }}<small v-if="p.type === 'discipline'">{{ p.settings.discipline }}</small></span>
                <span class="rt-iacts">
                  <button v-if="rowNeedsConfig(p)" type="button" class="rt-ibtn" :class="{ on: openConfigId === p.id }" :title="$t('configuration')" @click="toggleConfig(p.id)">
                    <i class="fas fa-cog" aria-hidden="true"></i>
                  </button>
                  <button type="button" class="rt-ibtn" :disabled="template.locked || i === 0" :title="$t('move_up')" @click="move(i, -1)">
                    <i class="fas fa-chevron-up" aria-hidden="true"></i>
                  </button>
                  <button type="button" class="rt-ibtn" :disabled="template.locked || i === pages.length - 1" :title="$t('move_down')" @click="move(i, 1)">
                    <i class="fas fa-chevron-down" aria-hidden="true"></i>
                  </button>
                  <button type="button" class="rt-ibtn rt-eye" :class="{ on: p.visible }" :disabled="template.locked" :title="p.visible ? $t('hide') : $t('show')" @click="toggleVis(i)">
                    <i class="fas" :class="p.visible ? 'fa-eye' : 'fa-eye-slash'" aria-hidden="true"></i>
                  </button>
                </span>
              </div>

              <!-- Config inline restringida: portada (imagen + título/subtítulo si existen) o
                   grupos del dashboard cuando es custom. No abre el Constructor. -->
              <div v-if="openConfigId === p.id" class="rt-item-cfg">
                <template v-if="p.type === 'cover'">
                  <label class="rt-cfg-lbl">{{ $t('cover_image') }}</label>
                  <label class="rt-drop">
                    <img v-if="coverSrc" :src="coverSrc" alt="">
                    <span v-else class="rt-drop-txt"><i class="fas fa-arrow-up-from-bracket" aria-hidden="true"></i> {{ $t('upload_image') }}</span>
                    <input type="file" accept="image/jpg,image/jpeg,image/png" @change="onCoverFile">
                  </label>
                  <button v-if="coverSrc" type="button" class="rt-cover-del" @click="deleteCover">{{ $t('remove_image') }}</button>
                  <template v-if="coverHasTitle">
                    <label class="rt-cfg-lbl">{{ $t('title') }}</label>
                    <input type="text" class="rt-cfg-input" v-model="p.settings.title">
                  </template>
                  <template v-if="coverHasSubtitle">
                    <label class="rt-cfg-lbl">{{ $t('subtitle') }}</label>
                    <input type="text" class="rt-cfg-input" v-model="p.settings.subtitle">
                  </template>
                </template>

                <template v-else-if="p.type === 'discipline'">
                  <label class="rt-cfg-lbl">{{ $t('section_name') }} <span class="rt-cfg-code">{{ p.settings.discipline }}</span></label>
                  <input type="text" class="rt-cfg-input" v-model="p.settings.name" :placeholder="discName(p.settings.discipline)">
                </template>

                <template v-else-if="p.type === 'dashboard'">
                  <label class="rt-cfg-lbl">{{ $t('groups') }}</label>
                  <treeselect
                    v-model="p.settings.customGroups"
                    :multiple="true"
                    :flat="true"
                    :options="activityGroups"
                    :normalizer="treeNormalizer"
                    :placeholder="$t('select_groups')"
                    append-to-body />
                </template>
              </div>
            </li>
          </ul>
        </div>

        <div class="rt-pfoot">
          <button type="button" class="rt-btn-primary" @click="apply">{{ $t('apply_template') }}</button>
          <button type="button" class="rt-btn-ghost" @click="realPreview">{{ $t('preview_real_pdf') }}</button>
        </div>
      </div>

      <!-- Preview (contenido generado por render.js del builder; sin botón de config) -->
      <div class="rt-preview">
        <div class="rt-pbar">
          <span class="rt-pbar-t">{{ $t('preview') }}</span>
          <span class="rt-note">{{ sheets.length }} {{ sheets.length === 1 ? $t('page') : $t('pages') }}</span>
          <span class="flex-1"></span>
          <span class="rt-note">{{ $t('schematic_real_structure') }}</span>
        </div>
        <div class="rt-pviewer">
          <div class="rt-thumbrail" ref="rail">
            <div
              v-for="(sh, i) in sheets"
              :key="'t' + i"
              class="rt-trow"
              :class="{ active: activeThumb === i + 1 }"
              @click="goTo(i + 1)">
              <div class="rt-mini"><div class="rpb rt-thumb"><div class="sheet" :class="{ landscape: sh.land }" v-html="sh.html"></div></div></div>
              <div class="rt-cap"><b>{{ i + 1 }}</b> · {{ sh.cap }}</div>
            </div>
          </div>

          <div class="rt-pages-scroll" ref="scroller">
            <div v-if="!sheets.length" class="rt-empty">{{ $t('nothing_selected') }}</div>
            <div v-else class="rpb"><div class="rpb-preview" v-html="previewHtml"></div></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmación de aplicar plantilla (wrapper modal consistente) -->
    <epc-modal-wrapper :active.sync="confirmOpen" :title="$t('apply_template')" :width="480">
      <p class="text-sm text-gray-700 leading-relaxed">
        Se reemplazará la configuración actual del informe por la plantilla <b>{{ template.name }}</b>.
        Los grupos de las disciplinas se re-vinculan automáticamente.
      </p>
      <template #footer>
        <b-button @click="confirmOpen = false">{{ $t('cancel') }}</b-button>
        <b-button type="is-primary" :loading="applying" @click="confirmApply">{{ $t('apply_template') }}</b-button>
      </template>
    </epc-modal-wrapper>
  </section>
</template>

<script>
import { project as demoProject } from '../data/report-template.data'
import { DEFAULT_GROUPS, buildPreview, sheetsFor, sheetTitle, discName, TYPES } from '../../report-page-builder/data/page-builder.render'
import {
  buildCustomizerPages, flattenGroups, autoLinkPages, rowNeedsConfig as rowNeedsConfigFn,
  pageDisplayName, missingDisciplines, uploadCoverImage, persistCustomizerConfig,
} from '../services/report-customizer.service'
import EpcModalWrapper from '·/components/epc-modal-wrapper.vue'
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'

export default {
  name: 'template-report-detail',

  components: { EpcModalWrapper, Treeselect },

  props: {
    template: { type: Object, required: true },
    project: { type: Object, default: () => demoProject },
    projectId: { type: [Number, String], default: null },
    activityGroups: { type: Array, default: () => [] },
  },

  data() {
    return {
      pages: [],            // páginas de la plantilla (lista editable: visible + orden)
      activeThumb: 1,
      io: null,
      confirmOpen: false,   // modal de confirmación (epc-modal-wrapper)
      applying: false,
      openConfigId: null,   // id de la página cuyo panel de config inline está abierto
      coverPreview: '',     // objectURL de la imagen de portada recién elegida (no persiste)
      s3bucket: (window.config && window.config.s3bucket) || '',
    }
  },

  created() { this.pages = this.buildPages() },

  computed: {
    // Clave canónica (weekly/executive/custom). null cuando es una config traída de otro
    // proyecto (no es una plantilla con nombre → no se identifica como tal).
    templateKey() { return this.template.key || null },

    // Portada: imagen (archivo nuevo o key en BD) y si trae título/subtítulo definidos.
    coverPage() { return this.pages.find((p) => p.type === 'cover') || null },
    coverVisible() { return !!this.coverPage && this.coverPage.visible !== false },
    coverSrc() {
      if (this.coverPreview) return this.coverPreview
      const ic = this.coverPage && this.coverPage.settings && this.coverPage.settings.image_cover
      return (ic && ic.url) ? ('//' + this.s3bucket + '/' + ic.url) : ''
    },
    coverHasImage() {
      const ic = this.coverPage && this.coverPage.settings && this.coverPage.settings.image_cover
      return !!(this.coverPreview || (ic && (ic.file || ic.url)))
    },
    coverHasTitle() { return !!(this.coverPage && this.coverPage.settings && this.coverPage.settings.title) },
    coverHasSubtitle() { return !!(this.coverPage && this.coverPage.settings && this.coverPage.settings.subtitle) },

    ctx() {
      return {
        pages: this.pages,
        groups: DEFAULT_GROUPS,
        project: { name: this.project.name, company: this.project.company, week: this.project.weekNum || this.project.week || '' },
        s3bucket: this.s3bucket,
        coverPreview: this.coverPreview,
        activityGroups: this.activityGroups || [],
      }
    },

    // Árbol de grupos aplanado para el auto-enlace (servicio).
    flatGroups() { return flattenGroups(this.activityGroups) },

    previewHtml() { return buildPreview(this.ctx, 'full', null) },

    // Una entrada por HOJA generada (para el rail): miniatura = hoja real a escala.
    sheets() {
      const out = []
      this.pages.filter((p) => p.visible !== false).forEach((p) => {
        sheetsFor(p, this.ctx).forEach((sh) => out.push({ t: sh.t, html: sh.html, land: !!sh.land, cap: sh.t || sheetTitle(p) || TYPES[p.type].name }))
      })
      return out
    },
  },

  watch: {
    template() {
      this.pages = this.buildPages()
    },
    sheets() {
      this.$nextTick(() => { this.activeThumb = 1; this.observe() })
    },
  },

  mounted() {
    this.$nextTick(() => this.observe())
  },

  beforeDestroy() {
    if (this.io) this.io.disconnect()
  },

  methods: {
    discName,

    // Páginas editables (servicio: template.pages o plantilla canónica por key).
    buildPages() { return buildCustomizerPages(this.template, this.templateKey) },

    rowNeedsConfig(p) { return rowNeedsConfigFn(p) },
    toggleConfig(id) { this.openConfigId = this.openConfigId === id ? null : id },

    // Normalizador del treeselect de grupos (árbol de actividades → id/label/children).
    treeNormalizer(n) { return { id: n.id, label: n.label || n.name, children: n.children } },

    // Imagen de portada elegida (se sube a S3 al aplicar).
    onCoverFile(e) {
      const f = e.target.files && e.target.files[0]
      if (!f || !this.coverPage) return
      const ic = this.coverPage.settings.image_cover || (this.coverPage.settings.image_cover = {})
      ic.type = 'file'; ic.name = f.name; ic.mimeType = f.type; ic.file = f; ic.url = null
      if (this.coverPreview) URL.revokeObjectURL(this.coverPreview)
      this.coverPreview = URL.createObjectURL(f)
    },
    deleteCover() {
      if (!this.coverPage) return
      const ic = this.coverPage.settings.image_cover
      if (ic) { ic.file = null; ic.url = null; ic.name = null; ic.mimeType = null }
      if (this.coverPreview) { URL.revokeObjectURL(this.coverPreview); this.coverPreview = '' }
    },
    // Sube la portada pendiente a S3 (servicio) y limpia el preview local si subió.
    async uploadPendingCover() {
      const uploaded = await uploadCoverImage(this.coverPage)
      if (uploaded && this.coverPreview) { URL.revokeObjectURL(this.coverPreview); this.coverPreview = '' }
    },

    // Nombre visible de una página (servicio).
    displayName(p) { return pageDisplayName(p) },

    // Reordenar (bloqueado en plantillas fijas).
    move(i, dir) {
      if (this.template.locked) return
      const j = i + dir
      if (j < 0 || j >= this.pages.length) return
      const arr = this.pages.slice()
      const tmp = arr[i]; arr[i] = arr[j]; arr[j] = tmp
      this.pages = arr
    },

    // Mostrar/ocultar una página (bloqueado en plantillas fijas).
    toggleVis(i) {
      if (this.template.locked) return
      const p = this.pages[i]
      if (p) this.$set(p, 'visible', !p.visible)
    },

    goTo(num) {
      const scroller = this.$refs.scroller
      if (!scroller) return
      const sheet = scroller.querySelectorAll('.sheet')[num - 1]
      if (!sheet) return
      scroller.scrollTo({ top: sheet.offsetTop - scroller.offsetTop - 6, behavior: 'smooth' })
      this.activeThumb = num
    },
    observe() {
      if (this.io) this.io.disconnect()
      const scroller = this.$refs.scroller
      if (!scroller) return
      const sheets = Array.prototype.slice.call(scroller.querySelectorAll('.sheet'))
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const idx = sheets.indexOf(e.target)
            if (idx >= 0) this.activeThumb = idx + 1
          }
        })
      }, { root: scroller, threshold: 0.5 })
      sheets.forEach((s) => this.io.observe(s))
    },

    // Auto-enlace disciplina↔grupo (servicio).
    autoLinkedPages() { return autoLinkPages(this.pages, this.flatGroups) },

    apply() {
      if (!this.projectId) {
        this.$buefy.toast.open({ message: this.$t('error_loading_data'), type: 'is-danger' })
        return
      }
      // BLOQUEA: dashboard VISIBLE en modo custom debe tener grupos seleccionados.
      const dash = this.pages.find((p) => p.type === 'dashboard' && p.visible !== false)
      if (dash && dash.settings.showGroupProgress === 'custom' && !(dash.settings.customGroups && dash.settings.customGroups.length)) {
        this.openConfigId = dash.id
        this.$buefy.toast.open({ message: this.$t('dashboard_groups_required'), type: 'is-danger', duration: 5000, position: 'is-top' })
        return
      }
      // BLOQUEA: cada disciplina VISIBLE debe poder enlazarse a un grupo (code_custom/nombre).
      const missing = missingDisciplines(this.autoLinkedPages())
      if (missing.length) {
        this.$buefy.toast.open({
          message: 'No se aplicó: no existe grupo para ' + missing.join(', ') + '. Crea/renombra esos grupos o desactiva esas disciplinas.',
          type: 'is-danger', duration: 6000, position: 'is-top',
        })
        return
      }
      // AVISA (no bloquea): portada visible sin imagen.
      if (this.coverVisible && !this.coverHasImage) {
        this.$buefy.toast.open({ message: this.$t('cover_image_missing_warning'), type: 'is-warning', duration: 4000, position: 'is-top' })
      }
      this.confirmOpen = true
    },

    async confirmApply() {
      if (this.applying) return
      this.applying = true
      try {
        await this.uploadPendingCover() // sube la portada (si hay archivo) antes de persistir
        const pages = this.autoLinkedPages() // recalcula tras subir la portada
        const importedFrom = this.template.sourceId ? { id: this.template.sourceId, name: this.template.name } : null
        await persistCustomizerConfig({ projectId: this.projectId, pages, templateKey: this.templateKey, importedFrom })
        this.confirmOpen = false
        this.$buefy.toast.open({ message: this.$t('configuration_saved'), type: 'is-success', duration: 3000 })
      } catch (e) {
        this.$buefy.toast.open({ message: this.$t('error_saving_configuration'), type: 'is-danger', duration: 4000 })
      } finally {
        this.applying = false
      }
    },

    realPreview() {
      this.$buefy.toast.open({ message: this.$t('real_preview_simulated'), type: 'is-info' })
    },
  },
}
</script>

<style scoped>
/* Customizer */
.rt-back { display: inline-flex; align-items: center; gap: 6px; color: var(--rt-muted); font-size: 13px; font-weight: 700; background: none; border: none; cursor: pointer; margin-bottom: 14px; }
.rt-back:hover { color: var(--rt-ink); }
.rt-split { display: grid; grid-template-columns: 330px 1fr; gap: 20px; align-items: start; }
.rt-panel { background: var(--rt-surface); border: 1px solid var(--rt-line); border-radius: var(--rt-radius); box-shadow: var(--rt-shadow); display: flex; flex-direction: column; height: min(80vh, 720px); overflow: hidden; }
.rt-phead { padding: 14px 16px; border-bottom: 1px solid var(--rt-line); flex: none; }
.rt-pname { font-size: 16px; font-weight: 800; }
.rt-psub { font-size: 12px; color: var(--rt-muted); }
.rt-locknote { margin-top: 8px; font-size: 11.5px; font-weight: 700; color: var(--rt-warn); }
.rt-pbody { overflow-y: auto; flex: 1; }

/* Lista de páginas (mostrar/ocultar + reordenar + config inline restringida) */
.rt-list { list-style: none; margin: 0; padding: 12px; display: flex; flex-direction: column; gap: 6px; }
.rt-item { display: flex; flex-direction: column; gap: 8px; padding: 8px 10px; border: 1px solid var(--rt-line); border-radius: 9px; background: var(--rt-surface); }
.rt-item.off { opacity: .55; background: var(--rt-surface-2); }
.rt-item.expanded { border-color: var(--rt-line-strong); }
.rt-item-row { display: flex; align-items: center; gap: 10px; }
.rt-inum { width: 20px; text-align: center; font-size: 11px; font-weight: 800; color: var(--rt-faint); flex: none; }
.rt-iname { flex: 1; min-width: 0; font-size: 13.5px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.rt-iname small { display: block; font-weight: 600; font-size: 10.5px; letter-spacing: .04em; color: var(--rt-faint); }
.rt-iacts { display: inline-flex; gap: 2px; flex: none; }
.rt-ibtn { appearance: none; border: none; background: none; color: var(--rt-muted); width: 28px; height: 28px; border-radius: 7px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; font-size: 13px; }
.rt-ibtn:hover:not(:disabled) { background: var(--rt-surface-2); color: var(--rt-ink); }
.rt-ibtn:disabled { opacity: .35; cursor: not-allowed; }
.rt-ibtn.on { color: var(--rt-accent); background: var(--rt-accent-wash); }
.rt-eye.on { color: var(--rt-accent); }

/* Panel de configuración inline (portada / grupos del dashboard custom) */
.rt-item-cfg { border-top: 1px dashed var(--rt-line); padding-top: 8px; display: flex; flex-direction: column; gap: 6px; }
.rt-cfg-lbl { font-size: 11px; font-weight: 700; color: var(--rt-muted); display: flex; align-items: center; gap: 6px; }
.rt-cfg-code { font-size: 10px; font-weight: 800; color: var(--rt-accent-ink); background: var(--rt-accent-wash); border-radius: 999px; padding: 1px 7px; }
.rt-cfg-input { font: inherit; font-size: 13px; padding: 7px 9px; border: 1px solid var(--rt-line-strong); border-radius: 8px; background: var(--rt-surface); color: var(--rt-ink); }
.rt-drop { display: flex; align-items: center; justify-content: center; height: 84px; border: 1.5px dashed var(--rt-line-strong); border-radius: 10px; cursor: pointer; overflow: hidden; background: var(--rt-surface-2); }
.rt-drop:hover { border-color: var(--rt-accent); }
.rt-drop img { width: 100%; height: 100%; object-fit: cover; }
.rt-drop input[type="file"] { display: none; }
.rt-drop-txt { font-size: 12px; font-weight: 600; color: var(--rt-muted); display: inline-flex; align-items: center; gap: 7px; }
.rt-cover-del { align-self: flex-start; appearance: none; border: none; background: none; color: var(--rt-warn); font: inherit; font-size: 12px; font-weight: 700; cursor: pointer; padding: 0; }
.rt-pfoot { padding: 14px 16px; display: flex; flex-direction: column; gap: 8px; border-top: 1px solid var(--rt-line); flex: none; background: var(--rt-surface); }
.rt-btn-primary { font: inherit; font-size: 13px; font-weight: 700; border-radius: 9px; padding: 9px 14px; cursor: pointer; border: 1px solid transparent; background: var(--rt-accent); color: #fff; }
.rt-btn-primary:hover { background: var(--rt-accent-ink); }
.rt-btn-ghost { font: inherit; font-size: 13px; font-weight: 700; border-radius: 9px; padding: 9px 14px; cursor: pointer; background: transparent; color: var(--rt-ink); border: 1px solid var(--rt-line-strong); }
.rt-btn-ghost:hover { background: var(--rt-surface-2); }

/* Preview (layout por página; las hojas las pinta render.js dentro de .rpb) */
.rt-preview { min-width: 0; }
.rt-pbar { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.rt-pbar-t { font-size: 12px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; color: var(--rt-faint); }
.rt-note { font-size: 11.5px; color: var(--rt-faint); }
.flex-1 { flex: 1; }
.rt-pviewer { display: grid; grid-template-columns: 128px 1fr; gap: 12px; align-items: start; }
.rt-thumbrail { display: flex; flex-direction: column; gap: 8px; max-height: min(74vh, 700px); overflow-y: auto; padding: 2px 6px 2px 2px; }
.rt-trow { border: 1px solid var(--rt-line); border-radius: 7px; background: var(--rt-surface); padding: 5px; cursor: pointer; transition: border-color .12s, box-shadow .12s; }
.rt-trow:hover { border-color: var(--rt-line-strong); }
.rt-trow.active { border-color: var(--rt-accent); box-shadow: 0 0 0 2px var(--rt-accent-wash); }
/* Miniatura = hoja real de render.js escalada (representación fiel) */
.rt-mini { width: 104px; height: 148px; overflow: hidden; border: 1px solid var(--rt-line); border-radius: 4px; background: #fff; margin: 0 auto; }
.rt-thumb { transform: scale(.27); transform-origin: top left; width: 385px; pointer-events: none; }
.rt-cap { font-size: 9px; font-weight: 700; color: var(--rt-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; text-align: center; margin-top: 4px; }
.rt-cap b { color: var(--rt-faint); margin-right: 2px; }
.rt-pages-scroll { max-height: min(74vh, 700px); overflow-y: auto; background: var(--rt-surface-2); border: 1px solid var(--rt-line); border-radius: 12px; padding: 14px; scroll-behavior: smooth; }
.rt-empty { color: var(--rt-faint); font-size: 13px; text-align: center; padding: 48px 10px; }

@media (max-width: 820px) {
  .rt-split { grid-template-columns: 1fr; }
  .rt-panel { height: auto; }
  .rt-pviewer { grid-template-columns: 1fr; }
  .rt-thumbrail { flex-direction: row; max-height: none; overflow-x: auto; }
  .rt-trow { flex: none; }
}
</style>
